import React, { useCallback, useRef, useState } from 'react';

import { Button, passReadyMessage } from '@copilot/ui';
import { PRODUCT_NAME } from '../constants/brand';
import { ANALYTICS_EVENTS, track } from '../services/analytics';
import { reportError } from '../services/errorTracking';
import * as billingApi from '../services/billingApi';
import * as meetingPassApi from '../services/meetingPassApi';

// Web's Pay Per Meeting checkout: a ONE-TIME Razorpay order, run in the
// page. The counterpart of useRazorpayCheckout for something that is
// paid once - it never opens a subscription and never touches the plan.
//
// start() resolves once the flow is over: 'available' (the backend has
// confirmed the payment with the provider), 'delayed' (paid, not yet
// confirmed) or 'dismissed' (closed without paying). It rejects when
// checkout could not open or the payment could not be confirmed.
//
// Nothing here decides that a pass exists. "Available" is only ever what
// the backend answered after checking with the provider.
export function useMeetingPassCheckout({ user, onPurchased } = {}) {
  // { stage: 'opening'|'awaiting_payment'|'confirming'|'available'|'delayed', message }
  const [stage, setStage] = useState(null);
  const pending = useRef(null);
  const onPurchasedRef = useRef(onPurchased);
  onPurchasedRef.current = onPurchased;

  // Asks the backend again until it reports the payment confirmed. Each
  // call makes the backend re-check with the provider, so this is a wait
  // for confirmation, never a way to obtain one.
  const confirm = useCallback(async (handlerResponse, attempts = billingApi.ACTIVATION_POLL_ATTEMPTS) => {
    let status = await meetingPassApi.acknowledgeCheckout(handlerResponse);

    for (let attempt = 0; attempt < attempts && status.status === 'processing'; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, billingApi.ACTIVATION_POLL_INTERVAL_MS);
      });

      try {
        // eslint-disable-next-line no-await-in-loop
        status = await meetingPassApi.acknowledgeCheckout(handlerResponse);
      } catch (_) {
        // A failed re-check is not a failed payment - keep trying.
      }
    }

    return status;
  }, []);

  const settle = useCallback((status) => {
    if (status.status === 'processing') {
      // Paid, but not confirmed yet. Never say "failed" here, and never
      // say the meeting is ready.
      setStage({
        stage: 'delayed',
        message:
          'Payment received. We are still confirming it - your meeting will be added as soon as it is. ' +
          'Check again in a moment or contact support if it does not update.',
      });
      return 'delayed';
    }

    pending.current = null;
    setStage({ stage: 'available', message: passReadyMessage(status.product) });
    track(ANALYTICS_EVENTS.PURCHASE_COMPLETED, { kind: 'meeting_pass' });
    onPurchasedRef.current?.(status);
    return 'available';
  }, []);

  const start = useCallback(async () => {
    setStage({ stage: 'opening', message: 'Opening checkout…' });

    let checkout;
    let Razorpay;
    try {
      [checkout, Razorpay] = await Promise.all([
        meetingPassApi.startCheckout(),
        billingApi.loadCheckoutScript(),
      ]);
    } catch (err) {
      setStage(null);
      track(ANALYTICS_EVENTS.PURCHASE_FAILED, {
        kind: 'meeting_pass',
        reason: err?.code || 'checkout_unavailable',
      });
      // Category and code only - never payment details (services/monitoring.js).
      reportError('billing_checkout', err);
      throw err;
    }

    track(ANALYTICS_EVENTS.CHECKOUT_STARTED, { kind: 'meeting_pass', plan_code: 'meeting_pass' });
    setStage({ stage: 'awaiting_payment', message: 'Complete the payment in the Razorpay window.' });

    return new Promise((resolve, reject) => {
      // An ORDER, not a subscription: paid once, nothing recurs. The
      // provider takes the amount from the order it already holds -
      // nothing passed here can change what is charged.
      const instance = new Razorpay({
        key: checkout.key_id,
        order_id: checkout.provider_order_id,
        name: PRODUCT_NAME,
        description: `${checkout.name} — ${checkout.description}`,
        prefill: { email: user?.email, name: user?.full_name },
        handler: async (response) => {
          pending.current = response;
          setStage({ stage: 'confirming', message: 'Payment received. Confirming…' });
          try {
            resolve(settle(await confirm(response)));
          } catch (err) {
            // The payment may well have succeeded - only the
            // confirmation failed. The backend reconciles it.
            setStage(null);
            track(ANALYTICS_EVENTS.PURCHASE_FAILED, { kind: 'meeting_pass', reason: 'confirmation_failed' });
            // Category and code only - never payment details (services/monitoring.js).
            reportError('billing_confirmation', err);
            reject(
              new Error(
                err?.message ||
                  'We could not confirm your payment. If money was taken it will be reconciled automatically.'
              )
            );
          }
        },
        modal: {
          // Closed without paying - not an error.
          ondismiss: () => {
            setStage(null);
            resolve('dismissed');
          },
        },
      });
      instance.open();
    });
  }, [confirm, settle, user]);

  // A manual re-check after confirmation was reported as still pending.
  const recheck = useCallback(async () => {
    if (!pending.current) return;
    setStage({ stage: 'confirming', message: 'Checking again…' });
    try {
      settle(await confirm(pending.current, 3));
    } catch (_) {
      settle({ status: 'processing' });
    }
  }, [confirm, settle]);

  const isBusy = Boolean(stage) && !['available', 'delayed'].includes(stage.stage);

  return { stage, start, recheck, isBusy };
}

// The purchase's progress, shown beside the Buy button.
export function MeetingPassCheckoutNotice({ stage, onRecheck }) {
  if (!stage) return null;
  return (
    <div className={`form-alert billing-alert-notice checkout-stage-${stage.stage}`} role="status">
      {stage.message}
      {stage.stage === 'delayed' && (
        <div className="checkout-stage-actions">
          <Button variant="secondary" onClick={onRecheck}>
            Check again
          </Button>
        </div>
      )}
    </div>
  );
}
